/**
 * DRIS Corporate Identity Typography System
 *
 * Headlines: Ubuntu (Bold & Semi-bold)
 * Body Text: Urbanist
 *
 * This centralized configuration ensures consistent typography across
 * the entire DRIS application following the Corporate Identity guidelines.
 */

export const drisFontFamilies = {
  // Headlines use Ubuntu
  headline: '"Ubuntu", "Helvetica Neue", Helvetica, Arial, sans-serif',
  // Body text uses Urbanist
  body: '"Urbanist", "Helvetica Neue", Helvetica, Arial, sans-serif',
  // Monospace for code/data
  mono: '"Roboto Mono", "Courier New", Courier, monospace',
  // Fallback to Inter for special cases
  fallback: '"Inter", "Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif',
};

export const drisFontWeights = {
  light: 300,
  regular: 400,
  medium: 500,
  semibold: 500,  // Ubuntu uses 500 for semi-bold
  bold: 700,
};

export const drisTypography = {
  // Headlines - Ubuntu Bold
  h1: {
    fontFamily: drisFontFamilies.headline,
    fontWeight: drisFontWeights.bold,
    fontSize: '2.5rem',
    lineHeight: 1.2,
    letterSpacing: '-0.01562em',
  },
  h2: {
    fontFamily: drisFontFamilies.headline,
    fontWeight: drisFontWeights.bold,
    fontSize: '2rem',
    lineHeight: 1.3,
    letterSpacing: '-0.00833em',
  },
  h3: {
    fontFamily: drisFontFamilies.headline,
    fontWeight: drisFontWeights.bold,
    fontSize: '1.75rem',
    lineHeight: 1.4,
    letterSpacing: '0em',
  },
  h4: {
    fontFamily: drisFontFamilies.headline,
    fontWeight: drisFontWeights.semibold,
    fontSize: '1.5rem',
    lineHeight: 1.4,
    letterSpacing: '0.00735em',
  },
  h5: {
    fontFamily: drisFontFamilies.headline,
    fontWeight: drisFontWeights.semibold,
    fontSize: '1.25rem',
    lineHeight: 1.5,
    letterSpacing: '0em',
  },
  h6: {
    fontFamily: drisFontFamilies.headline,
    fontWeight: drisFontWeights.semibold,
    fontSize: '1.125rem',
    lineHeight: 1.6,
    letterSpacing: '0.0075em',
  },
  // Subtitles - Ubuntu Semi-bold
  subtitle1: {
    fontFamily: drisFontFamilies.headline,
    fontWeight: drisFontWeights.semibold,
    fontSize: '1rem',
    lineHeight: 1.75,
    letterSpacing: '0.00938em',
  },
  subtitle2: {
    fontFamily: drisFontFamilies.headline,
    fontWeight: drisFontWeights.medium,
    fontSize: '0.875rem',
    lineHeight: 1.57,
    letterSpacing: '0.00714em',
  },
  // Body text - Urbanist
  body1: {
    fontFamily: drisFontFamilies.body,
    fontWeight: drisFontWeights.regular,
    fontSize: '1rem',
    lineHeight: 1.5,
    letterSpacing: '0.00938em',
  },
  body2: {
    fontFamily: drisFontFamilies.body,
    fontWeight: drisFontWeights.regular,
    fontSize: '0.875rem',
    lineHeight: 1.43,
    letterSpacing: '0.01071em',
  },
  // Buttons - Ubuntu Semi-bold
  button: {
    fontFamily: drisFontFamilies.headline,
    fontWeight: drisFontWeights.semibold,
    fontSize: '0.875rem',
    lineHeight: 1.75,
    letterSpacing: '0.02857em',
    textTransform: 'uppercase' as const,
  },
  // Captions - Urbanist
  caption: {
    fontFamily: drisFontFamilies.body,
    fontWeight: drisFontWeights.regular,
    fontSize: '0.75rem',
    lineHeight: 1.66,
    letterSpacing: '0.03333em',
  },
  // Overline - Ubuntu
  overline: {
    fontFamily: drisFontFamilies.headline,
    fontWeight: drisFontWeights.medium,
    fontSize: '0.75rem',
    lineHeight: 2.66,
    letterSpacing: '0.08333em',
    textTransform: 'uppercase' as const,
  },
  // Data tables and code - Monospace
  code: {
    fontFamily: drisFontFamilies.mono,
    fontWeight: drisFontWeights.regular,
    fontSize: '0.875rem',
    lineHeight: 1.5,
  },
};

// MUI Typography configuration
export const muiTypographyConfig = {
  fontFamily: drisFontFamilies.body,
  fontSize: 14,
  htmlFontSize: 16,
  fontWeightLight: drisFontWeights.light,
  fontWeightRegular: drisFontWeights.regular,
  fontWeightMedium: drisFontWeights.medium,
  fontWeightBold: drisFontWeights.bold,
  h1: drisTypography.h1,
  h2: drisTypography.h2,
  h3: drisTypography.h3,
  h4: drisTypography.h4,
  h5: drisTypography.h5,
  h6: drisTypography.h6,
  subtitle1: drisTypography.subtitle1,
  subtitle2: drisTypography.subtitle2,
  body1: drisTypography.body1,
  body2: drisTypography.body2,
  button: drisTypography.button,
  caption: drisTypography.caption,
  overline: drisTypography.overline,
};

// CSS Variables for direct usage
export const typographyCSSVariables = `
  --dris-font-headline: ${drisFontFamilies.headline};
  --dris-font-body: ${drisFontFamilies.body};
  --dris-font-mono: ${drisFontFamilies.mono};
  --dris-font-fallback: ${drisFontFamilies.fallback};

  --dris-font-weight-light: ${drisFontWeights.light};
  --dris-font-weight-regular: ${drisFontWeights.regular};
  --dris-font-weight-medium: ${drisFontWeights.medium};
  --dris-font-weight-semibold: ${drisFontWeights.semibold};
  --dris-font-weight-bold: ${drisFontWeights.bold};
`;